/**
 * Service layer for the Lists domain.
 * All functions communicate with the backend REST API via apiClient.
 */
import apiClient from "modules/main/services/apiClient";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import {
    endPointListItemSearch,
    endPointListItems,
    endPointListItem,
    endPointLists,
    endPointListDetail,
    endPointListSeedTemplate,
    endPointListExecute,
    endPointListExecuteStream,
    endPointListMigrateStream,
    endPointListExecution,
    endPointListCandidateSelect,
} from "modules/main/services/endpoints";
import { CandidateProduct, CreateListItemPayload, ItemSearchResult, List, ListCategorySummary, ListDetail, ListExecution, ListExecutionStatus, ListItem, ListItemType, MigrationState, MigrationStep, ProgressState, ProgressStep, ResolvedListItem } from "modules/lists/types/lists";
import type { ProductImages } from "modules/product/types/product";
import { PlanRequiredBody, PlanRequiredError } from "modules/main/services/errors";

// ─── Backend response shapes (snake_case from the API) ──────────────────────

interface ListCategorySummaryApiResponse {
    category_id: number | null;
    item_count: number;
}

interface ListApiResponse {
    id: string;
    name: string;
    description: string | null;
    sort_order: number;
    created_at: string;
    updated_at: string;
    item_count: number;
    category_summaries: ListCategorySummaryApiResponse[];
}

interface ListItemApiResponse {
    id: string;
    list_id: string;
    raw_description: string;
    catalog_product_id: string | null;
    linked_product_name: string | null;
    linked_product_sku: string | null;
    category_id: number | null;
    quantity: number;
    unit: string | null;
    notes: string | null;
    sort_order: number;
    type: string; // 'linked' | 'free'
}

interface ListDetailApiResponse extends Omit<ListApiResponse, "item_count" | "category_summaries"> {
    items: ListItemApiResponse[];
    /** Optional for backward compatibility: older backends omit migration fields. */
    migration_required?: boolean;
    migration_total?: number;
    migration_completed?: number;
}

export type SeedListTemplateKey = "onboarding-default";

// ─── Mapper helpers ──────────────────────────────────────────────────────────

const mapList = (r: ListApiResponse): List => ({
    id: r.id,
    name: r.name,
    description: r.description,
    sortOrder: r.sort_order,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
    itemCount: r.item_count,
    categorySummaries: r.category_summaries.map((summary): ListCategorySummary => ({
        categoryId: summary.category_id,
        itemCount: summary.item_count,
    })),
});

const mapListItem = (r: ListItemApiResponse): ListItem => ({
    id: r.id,
    listId: r.list_id,
    rawDescription: r.raw_description,
    catalogProductId: r.catalog_product_id,
    linkedProductName: r.linked_product_name,
    catalogProductSku: r.linked_product_sku,
    itemImgUrl: null,
    categoryId: r.category_id,
    quantity: r.quantity,
    unit: r.unit,
    notes: r.notes,
    sortOrder: r.sort_order,
    type: r.type === "linked" ? ListItemType.LINKED : ListItemType.FREE,
});

const getCategorySummariesFromItems = (items: ListItem[]): ListCategorySummary[] => {
    const counts = new Map<number | null, number>();

    items.forEach((item) => {
        const categoryId = item.categoryId ?? null;
        counts.set(categoryId, (counts.get(categoryId) ?? 0) + 1);
    });

    return Array.from(counts.entries()).map(([categoryId, itemCount]) => ({
        categoryId,
        itemCount,
    }));
};

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Fetches all active lists for the current user, ordered by sort_order.
 * @returns Array of List objects.
 */
export const fetchLists = async (): Promise<List[]> => {
    const data = await apiClient.get<ListApiResponse[]>(endPointLists);
    return data.map(mapList);
};

/**
 * Fetches full detail of a list, including its items.
 * @param listId UUID of the list.
 * @returns ListDetail with items array.
 */
export const fetchListDetail = async (listId: string): Promise<ListDetail> => {
    const data = await apiClient.get<ListDetailApiResponse>(endPointListDetail(listId));
    const items = data.items.map(mapListItem);

    return {
        id: data.id,
        name: data.name,
        description: data.description,
        sortOrder: data.sort_order,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        itemCount: items.length,
        categorySummaries: getCategorySummariesFromItems(items),
        items,
        migrationRequired: data.migration_required === true,
        migrationTotal: data.migration_total ?? 0,
        migrationCompleted: data.migration_completed ?? 0,
    };
};

/**
 * Seeds a backend-owned template into a list and maps the returned persisted detail.
 * @param listId UUID of the target list.
 * @param templateKey Supported template key.
 * @returns Refreshed ListDetail returned by the backend after idempotent seeding.
 */
export const seedListTemplate = async (
    listId: string,
    templateKey: SeedListTemplateKey,
): Promise<ListDetail> => {
    const data = await apiClient.post<ListDetailApiResponse>(
        endPointListSeedTemplate(listId),
        { template_key: templateKey },
    );
    const items = data.items.map(mapListItem);
    return {
        id: data.id,
        name: data.name,
        description: data.description,
        sortOrder: data.sort_order,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        itemCount: items.length,
        categorySummaries: getCategorySummariesFromItems(items),
        items,
        migrationRequired: data.migration_required === true,
        migrationTotal: data.migration_total ?? 0,
        migrationCompleted: data.migration_completed ?? 0,
    };
};

/**
 * Creates a new list for the current user.
 * @param name List name (max 120 chars).
 * @param description Optional description (max 255 chars).
 * @returns The newly created List.
 */
export const createList = async (name: string, description: string | null): Promise<List> => {
    const data = await apiClient.post<ListApiResponse>(endPointLists, {
        name,
        description: description || null,
    });
    return mapList(data);
};

/**
 * Updates the name and/or description of an existing list.
 * @param listId UUID of the list.
 * @param payload Fields to update (all optional).
 * @returns Updated List.
 */
export const updateList = async (
    listId: string,
    payload: Partial<{ name: string; description: string | null }>,
): Promise<List> => {
    const data = await apiClient.put<ListApiResponse>(endPointListDetail(listId), payload);
    return mapList(data);
};

/**
 * Soft-deletes a list.
 * @param listId UUID of the list.
 */
export const deleteList = async (listId: string): Promise<void> => {
    await apiClient.delete<void>(endPointListDetail(listId));
};

// ─── Item search API response shape ─────────────────────────────────────────

interface ItemSearchResultApiResponse {
    id: string;
    s_id: number;
    name: string;
    brand: string;
    image_url: string | null;
    price: number | null;
    unit: string | null;
    total: number | null;
    category_id: number;
    parsed_quantity: number;
    parsed_unit: string | null;
    hashed_sku: string | null;
}

interface ItemSearchApiResponse {
    search_term: string;
    parsed_quantity: number;
    parsed_unit: string | null;
    category_id: number | null;
    attribution_token: string;
    results: ItemSearchResultApiResponse[];
}

const mapItemSearchResult = (r: ItemSearchResultApiResponse): ItemSearchResult => ({
    id: r.id,
    s_id:r.s_id,
    name: r.name,
    brand: r.brand,
    imageUrl: r.image_url,
    price: r.price,
    unit: r.unit,
    total: r.total,
    categoryId: r.category_id,
    parsedQuantity: r.parsed_quantity,
    parsedUnit: r.parsed_unit,
    sku: r.hashed_sku,
});

/**
 * Searches for product candidates matching the user's raw description.
 * @param q Raw item description typed by the user.
 * @returns Parsed metadata + product candidates.
 */
export const searchItem = async (
    q: string,
    signal?: AbortSignal,
    skipParse?: boolean,
): Promise<{ searchTerm: string; parsedQuantity: number; parsedUnit: string | null; categoryId: number | null; results: ItemSearchResult[]; attribution_token: string }> => {
    const data = await apiClient.get<ItemSearchApiResponse>(endPointListItemSearch(q, skipParse), { signal });
    return {
        searchTerm: data.search_term,
        parsedQuantity: data.parsed_quantity,
        parsedUnit: data.parsed_unit,
        categoryId: data.category_id,
        results: data.results.map(mapItemSearchResult),
        attribution_token: data.attribution_token,
    };
};

/**
 * Adds a new item to an existing list.
 * @param listId UUID of the parent list.
 * @param payload Item creation payload.
 * @returns The newly created ListItem.
 */
/**
 * Updates an existing list item.
 * @param listId UUID of the parent list.
 * @param itemId UUID of the item to update.
 * @param payload Updated item fields.
 * @param signal Optional AbortSignal for request cancellation.
 * @returns The updated ListItem.
 */
export const updateItem = async (
    listId: string,
    itemId: string,
    payload: CreateListItemPayload,
    signal?: AbortSignal,
): Promise<ListItem> => {
    const data = await apiClient.put<ListItemApiResponse>(
        endPointListItem(listId, itemId),
        {
            raw_description: payload.rawDescription,
            catalog_product_id: payload.catalogProductId ?? null,
            catalog_product_sku: payload.catalogProductSku ?? null,
            category_id: payload.categoryId ?? null,
            quantity: payload.quantity,
            unit: payload.unit ?? null,
            notes: payload.notes ?? null,
        },
        { signal },
    );
    return mapListItem(data);
};

/**
 * Adds a new item to an existing list.
 * @param listId UUID of the parent list.
 * @param payload Item creation payload.
 * @returns The newly created ListItem.
 */
export const addItem = async (
    listId: string,
    payload: CreateListItemPayload,
): Promise<ListItem> => {
    const data = await apiClient.post<ListItemApiResponse>(endPointListItems(listId), {
        raw_description: payload.rawDescription,
        catalog_product_id: payload.catalogProductId ?? null,
        catalog_product_sku: payload.catalogProductSku ?? null,
        category_id: payload.categoryId ?? null,
        quantity: payload.quantity,
        unit: payload.unit ?? null,
        notes: payload.notes ?? null,
    });
    return mapListItem(data);
};

/**
 * Soft-deletes an item from a list.
 * @param listId UUID of the parent list.
 * @param itemId UUID of the item to delete.
 */
export const deleteItem = async (listId: string, itemId: string): Promise<void> => {
    await apiClient.delete<void>(endPointListItem(listId, itemId));
};

// ─── Execution API response shapes ──────────────────────────────────────────

interface CandidateProductApiResponse {
    id: string;
    catalog_product_id: number;
    canonical_sku: string;
    slug: string;
    name: string;
    brand: string | null;
    image_url?: string | null;
    /** Structured image set (thumbs/catalog/detail) — preferred over image_url. */
    images?: ProductImages | null;
    price: number | null;
    unit: string | null;
    confidence_score: number;
    rank: number;
    selected: boolean;
    sku?: string | null;
}

const mapCandidateProduct = (r: CandidateProductApiResponse): CandidateProduct => ({
    id: r.id,
    catalogProductId: r.catalog_product_id,
    canonicalSku: r.canonical_sku,
    slug: r.slug,
    name: r.name,
    brand: r.brand ?? "",
    imageUrl: r.image_url ?? r.images?.catalog ?? null,
    images: r.images ?? null,
    price: r.price,
    unit: r.unit,
    confidenceScore: r.confidence_score,
    rank: r.rank,
    selected: r.selected,
    sku: r.sku ?? null,
});

/**
 * Flat resolved item response from the API.
 * Replaces the nested ItemWithCandidatesApiResponse structure.
 */
interface ResolvedListItemApiResponse {
    id: string;
    list_id: string;
    raw_description: string;
    catalog_product_id: string | null;
    name: string | null;
    brand: string | null;
    image_url: string | null;
    price: number | null;
    category_id: number | null;
    quantity: number;
    unit: string | null;
    notes: string | null;
    sort_order: number;
    type: string;
    sku: string | null;
    confidence_score: number | null;
    selected_candidate_id: string | null;
    missed_reason: string | null;
    /** Candidates considered by the resolver — used as replacement alternatives. */
    candidates?: CandidateProductApiResponse[];
}

interface ListExecutionApiResponse {
    id: string;
    list_id: string;
    status: string;
    items_with_candidates: ResolvedListItemApiResponse[];
    missed_items: ResolvedListItemApiResponse[];
    temporary_items_with_candidates?: ResolvedListItemApiResponse[];
    cart_id: string | null;
    created_at: string;
}

const mapResolvedListItem = (r: ResolvedListItemApiResponse): ResolvedListItem => ({
    id: r.id,
    listId: r.list_id,
    rawDescription: r.raw_description,
    catalogProductId: r.catalog_product_id,
    linkedProductName: r.name,
    catalogProductSku: r.sku,
    itemImgUrl: r.image_url,
    categoryId: r.category_id,
    quantity: r.quantity,
    unit: r.unit,
    notes: r.notes,
    sortOrder: r.sort_order,
    type: r.type === "linked" ? ListItemType.LINKED : ListItemType.FREE,
    name: r.name,
    brand: r.brand ?? null,
    imageUrl: r.image_url,
    price: r.price,
    sku: r.sku,
    confidenceScore: r.confidence_score,
    selectedCandidateId: r.selected_candidate_id,
    missedReason: r.missed_reason,
    candidates: (r.candidates ?? []).map(mapCandidateProduct),
});

const mapListExecution = (r: ListExecutionApiResponse): ListExecution => ({
    id: r.id,
    listId: r.list_id,
    status: r.status as ListExecutionStatus,
    itemsWithCandidates: r.items_with_candidates.map(mapResolvedListItem),
    missedItems: (r.missed_items ?? []).map(mapResolvedListItem),
    temporaryItemsWithCandidates: (r.temporary_items_with_candidates ?? []).map(mapResolvedListItem),
    cartId: r.cart_id,
});

// ─── Execution public API ────────────────────────────────────────────────────

/**
 * Creates a new list execution and fires the LLM resolution synchronously.
 * Backend guarantees status 'draft' and candidates in the first response.
 * @param listId UUID of the list to execute.
 * @param locationId User's selected location ID for price hydration.
 * @param excludedItemIds Optional array of item IDs to exclude from execution.
 * @param itemQuantities Optional map of item ID to quantity override (item ID → quantity).
 * @param temporaryItems Optional array of temporary items to include in execution.
 * @returns ListExecution with resolved candidates and status 'draft'.
 */
export const executeList = async (
    listId: string,
    excludedItemIds?: string[],
    itemQuantities?: Record<string, number>,
    temporaryItems?: { tempKey: string; rawDescription: string; quantity: number }[],
): Promise<ListExecution> => {
    const data = await apiClient.post<ListExecutionApiResponse>(
        endPointListExecute(listId),
        {
            excluded_item_ids: excludedItemIds ?? [],
            item_quantities: itemQuantities
                ? Object.entries(itemQuantities).map(([item_id, quantity]) => ({ item_id, quantity }))
                : [],
            temporary_items: (temporaryItems ?? []).map((t) => ({
                temp_key: t.tempKey,
                raw_description: t.rawDescription,
                quantity: t.quantity,
            })),
        },
    );
    return mapListExecution(data);
};

/**
 * Fetches the current state of a list execution.
 * Retained as a future refresh/resume utility (e.g., manual refresh of resolution state).
 * @param executionId UUID of the execution.
 * @returns ListExecution with up-to-date candidates.
 */
export const getListExecution = async (
    executionId: string,
): Promise<ListExecution> => {
    const data = await apiClient.get<ListExecutionApiResponse>(
        endPointListExecution(executionId),
    );
    return mapListExecution(data);
};

/**
 * Changes the selected candidate for a specific list item inside an execution.
 * @param executionId UUID of the execution.
 * @param listItemId UUID of the list item.
 * @param candidateId UUID of the candidate to select.
 */
export const selectCandidate = async (
    executionId: string,
    listItemId: string,
    candidateId: string,
): Promise<void> => {
    await apiClient.patch<void>(endPointListCandidateSelect(executionId, listItemId), {
        candidate_id: candidateId,
    });
};

// ─── SSE progress stream ─────────────────────────────────────────────────────

/** Known progress steps emitted by the backend progress channel. */
const VALID_PROGRESS_STEPS = new Set<ProgressStep>([
    "starting",
    "embedding",
    "intent_search",
    "llm_batch",
    "completed",
    "failed",
]);

/**
 * Execution payload forwarded to the stream endpoint. Matches the JSON body
 * contract of the legacy `executeList` service call, so the stream executes
 * the list with the same exclusions, quantity overrides and temporary items.
 */
export interface ExecuteListStreamOptions {
    /** AbortSignal to close the stream (unmount / page lifecycle). */
    signal?: AbortSignal;
    /** Optional array of item IDs to exclude from execution. */
    excludedItemIds?: string[];
    /** Optional map of item ID to quantity override. */
    itemQuantities?: Record<string, number>;
    /** Optional free-text temporary items to include in execution. */
    temporaryItems?: { tempKey: string; rawDescription: string; quantity: number }[];
}

/**
 * Parses a raw SSE `data:` line into a typed ProgressState.
 * Pure function — no side effects, safe to unit test in isolation.
 * The terminal `completed` event maps its `payload` (snake_case
 * ListExecutionResponse from the backend) into a camelCase `ListExecution`,
 * and its `execution_id` into `executionId` — the recovery key when the
 * payload is absent.
 * @param data Raw JSON payload from one SSE frame (event: progress).
 * @returns Typed progress state with camelCase fields; `payload` and
 * `executionId` are null for every event except the terminal `completed` one.
 * @throws When the frame is not valid JSON or the step is not a known
 * progress step.
 */
export const parseProgressEvent = (data: string): ProgressState => {
    const raw = JSON.parse(data) as {
        step?: unknown;
        percentage?: unknown;
        message?: unknown;
        batch_info?: unknown;
        payload?: unknown;
        execution_id?: unknown;
    };

    if (typeof raw.step !== "string" || !VALID_PROGRESS_STEPS.has(raw.step as ProgressStep)) {
        throw new Error(`Unknown progress step: ${String(raw.step)}`);
    }

    const state: ProgressState = {
        step: raw.step as ProgressStep,
        percentage: typeof raw.percentage === "number" ? raw.percentage : 0,
        message: typeof raw.message === "string" ? raw.message : "",
        batchInfo: typeof raw.batch_info === "string" ? raw.batch_info : null,
        payload: null,
        executionId: typeof raw.execution_id === "string" ? raw.execution_id : null,
    };

    // The terminal completed event delivers the authoritative result.
    if (raw.payload !== null && typeof raw.payload === "object") {
        state.payload = mapListExecution(raw.payload as ListExecutionApiResponse);
    }

    return state;
};

/**
 * Reads a plan_required 403 body from an SSE response and throws
 * `PlanRequiredError` so the caller can branch on it (quota upsell UX).
 * @param response Non-ok fetchEventSource response (typically 403).
 * @throws PlanRequiredError when the body is `{error: "plan_required", ...}`.
 */
const throwIfPlanRequired = async (response: Response): Promise<void> => {
    if (response.ok || response.status !== 403) {
        return;
    }

    let body: Partial<PlanRequiredBody> | null = null;
    try {
        body = (await response.clone().json()) as Partial<PlanRequiredBody>;
    } catch {
        return; // non-JSON error body — not the plan_required contract
    }
    if (body?.error !== "plan_required") {
        return;
    }
    throw new PlanRequiredError({
        error: "plan_required",
        required_plan: body.required_plan ?? "plus",
        detail: body.detail ?? "Plan required",
        ...(body.reason ? { reason: body.reason } : {}),
        ...(body.quota ? { quota: body.quota } : {}),
    });
};

/**
 * Executes a list through the SSE stream endpoint and resolves with the final
 * `ListExecution` carried by the terminal `completed` event.
 *
 * This is the ONLY execution request the new frontend flow makes: the backend
 * runs the resolution pipeline, streams progress events to `onProgress`, and
 * delivers the authoritative result inside the completed event's payload.
 *
 * Cut 3: the backend quota gate returns 403 `plan_required` BEFORE the stream
 * opens. fetchEventSource bypasses apiClient, so the `onopen` handler parses
 * the 403 body and rejects with `PlanRequiredError` — never a null result —
 * letting the page show the quota upsell dialog instead of the generic error
 * toast.
 *
 * Recovery: when the terminal `completed` event arrives WITHOUT a usable
 * payload (status-only stream) but carries its `executionId`, the persisted
 * execution is fetched with a read-only GET (`getListExecution`) — never a
 * re-execution — so the stream-only flow still opens the cart after
 * completion.
 * @param listId UUID of the list being executed.
 * @param onProgress Callback invoked for every parsed progress event.
 * @param options Optional signal + execution payload (excluded ids, quantity
 * overrides, temporary items) forwarded in the POST body.
 * @returns The final ListExecution, or null when the stream ended without a
 * terminal result (failure, abort, or unrecoverable by-id fetch). Rejects
 * with `PlanRequiredError` when the quota gate blocked the execution.
 */
export const executeListStream = async (
    listId: string,
    onProgress: (progress: ProgressState) => void,
    options?: ExecuteListStreamOptions,
): Promise<ListExecution | null> => {
    let result: ListExecution | null = null;
    let recoveryExecutionId: string | null = null;
    try {
        await fetchEventSource(endPointListExecuteStream(listId), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            signal: options?.signal,
            body: JSON.stringify({
                excluded_item_ids: options?.excludedItemIds ?? [],
                item_quantities: options?.itemQuantities
                    ? Object.entries(options.itemQuantities).map(([item_id, quantity]) => ({ item_id, quantity }))
                    : [],
                temporary_items: (options?.temporaryItems ?? []).map((t) => ({
                    temp_key: t.tempKey,
                    raw_description: t.rawDescription,
                    quantity: t.quantity,
                })),
            }),
            onopen: async (response) => {
                // 403 quota gate fires before the stream opens — surface it
                // as a PlanRequiredError instead of a silent null result.
                await throwIfPlanRequired(response);
            },
            onmessage: (event) => {
                if (!event.data) return;
                try {
                    const parsed = parseProgressEvent(event.data);
                    // The terminal completed payload is the authoritative result.
                    if (parsed.step === "completed" && parsed.payload) {
                        result = parsed.payload;
                    } else if (parsed.step === "completed" && parsed.executionId) {
                        // Status-only completed event: remember the execution id
                        // so the persisted result can be recovered below with a
                        // read-only GET (never a re-execution).
                        recoveryExecutionId = parsed.executionId;
                    }
                    onProgress(parsed);
                } catch {
                    // Malformed frame — progress is best-effort, never fatal.
                }
            },
            onerror: (error) => {
                // Quota blocks reject the stream so the page can show the
                // upsell dialog; other stream errors keep the existing
                // no-false-success resolution (null result).
                if (error instanceof PlanRequiredError) throw error;
            },
        });
        if (result) {
            return result;
        }
        // Terminal completed arrived without a payload — recover the persisted
        // execution by id (read-only GET, no re-execution). A failed fetch
        // returns null so the caller shows the failure toast (no false success).
        if (recoveryExecutionId) {
            try {
                return await getListExecution(recoveryExecutionId);
            } catch {
                return null;
            }
        }
        return null;
    } catch (error) {
        // Abort (client close) or network failure before the terminal event
        // still returns the captured result; otherwise null (no false success).
        // Quota blocks must propagate as rejections — never a null result.
        if (error instanceof PlanRequiredError) throw error;
        return result;
    }
};

// ─── Migration SSE stream ────────────────────────────────────────────────────

/** Known steps emitted by the backend migration channel. */
const VALID_MIGRATION_STEPS = new Set<MigrationStep>([
    "migration_item",
    "migration_completed",
    "migration_partial",
]);

/**
 * Parses a raw SSE `data:` line into a typed MigrationState.
 *
 * Pure function — safe to unit test in isolation. Malformed frames and
 * unknown/missing steps return null (the migration stream is best-effort:
 * a bad frame must never crash the overlay or trigger an error state).
 *
 * @param data Raw JSON payload from one SSE frame (event: migration).
 * @returns Typed migration state with camelCase fields, or null when the
 * frame is malformed or carries an unknown step.
 */
export const parseMigrationEvent = (data: string): MigrationState | null => {
    let raw: {
        step?: unknown;
        percentage?: unknown;
        message?: unknown;
        item_id?: unknown;
        failed_count?: unknown;
        total_count?: unknown;
    };
    try {
        raw = JSON.parse(data) as typeof raw;
    } catch {
        return null;
    }

    if (typeof raw.step !== "string" || !VALID_MIGRATION_STEPS.has(raw.step as MigrationStep)) {
        return null;
    }

    return {
        step: raw.step as MigrationStep,
        percentage: typeof raw.percentage === "number" ? raw.percentage : 0,
        message: typeof raw.message === "string" ? raw.message : "",
        itemId: typeof raw.item_id === "string" ? raw.item_id : null,
        failedCount: typeof raw.failed_count === "number" ? raw.failed_count : null,
        totalCount: typeof raw.total_count === "number" ? raw.total_count : null,
    };
};

/**
 * Opens the migration SSE stream for a list and returns a cleanup function.
 *
 * The stream posts to `POST /lists/{id}/migrate/stream` and delivers typed
 * `MigrationState` events to `onEvent` as items are migrated. Terminal events
 * (`migration_completed` / `migration_partial`) close the stream internally —
 * no further events are delivered after them. Network/protocol failures call
 * `onError` once and close (no automatic retry; the caller decides).
 *
 * @param listId UUID of the list being migrated.
 * @param onEvent Callback invoked for every parsed migration event.
 * @param onError Callback invoked when the stream fails before a terminal event.
 * @returns A cleanup function that closes the stream (aborts the request).
 */
export const fetchMigrationStream = (
    listId: string,
    onEvent: (state: MigrationState) => void,
    onError: () => void,
): (() => void) => {
    const controller = new AbortController();
    let finished = false;

    const close = (): void => {
        if (finished) return;
        finished = true;
        controller.abort();
    };

    void fetchEventSource(endPointListMigrateStream(listId), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        signal: controller.signal,
        onmessage: (event) => {
            if (!event.data || finished) return;
            const state = parseMigrationEvent(event.data);
            if (!state) return; // Malformed frame — best-effort, ignore.
            onEvent(state);
            if (state.step === "migration_completed" || state.step === "migration_partial") {
                // Terminal event: close the channel; no further events arrive.
                close();
            }
        },
        onerror: () => {
            if (finished) return;
            finished = true;
            onError();
        },
    }).catch(() => {
        // Abort/failure is already handled via onerror; nothing to surface.
    });

    return () => {
        finished = true;
        controller.abort();
    };
};
